import { ActionIconProps, Mark, Text, UnstyledButton } from "@mantine/core";
import { modals } from "@mantine/modals";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PropsWithChildren, forwardRef } from "react";
import useUserStore from "@/modules/users/store";
import { captureException } from "@/services/log";
import { REGULAMENTOS_DESAFIOS_COLLECTION_KEY, deleteRegulamentoDesafios } from "../../firestore";

type RemoverButtonProperties = PropsWithChildren<
  ActionIconProps & {
    onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
    regulamento: TRegulamentoDesafios;
  }
>;
type TDeleteResponse = Awaited<ReturnType<typeof deleteRegulamentoDesafios>>;

const RemoverRegulamentoDesafiosButton = forwardRef<HTMLButtonElement, RemoverButtonProperties>(
  ({ regulamento, children, ...properties }, reference) => {
    const queryClient = useQueryClient();
    const activeEmpresaId = useUserStore((state) => state.activeEmpresaId);

    const { mutate: deleteRegulamentoDesafiosMutation, isPending } = useMutation<TDeleteResponse, Error>({
      mutationFn: () => {
        if (!activeEmpresaId) {
          throw new Error("Empresa não selecionada");
        }
        return deleteRegulamentoDesafios(activeEmpresaId, regulamento);
      },
      onSuccess() {
        return queryClient.invalidateQueries({ queryKey: [activeEmpresaId, ...REGULAMENTOS_DESAFIOS_COLLECTION_KEY] });
      },
      onError(error) {
        captureException(error, true);
      }
    });

    /**
     * Handles the click event on the button.
     * @param event - The event that triggered the click.
     */
    function handleClick(event: React.MouseEvent<HTMLButtonElement>) {
      event.preventDefault();

      if (properties.onClick) {
        properties.onClick(event);
      }

      modals.openConfirmModal({
        title: "Remover regulamento",
        centered: true,
        children: (
          <Text size="sm">
            Tem certeza que deseja remover o regulamento <Mark>{regulamento.nome}</Mark>?
          </Text>
        ),
        labels: {
          confirm: "Sim",
          cancel: "Não"
        },
        confirmProps: { color: "red" },
        onConfirm: () => {
          void deleteRegulamentoDesafiosMutation();
        }
      });
    }

    return (
      <UnstyledButton disabled={isPending} ref={reference} {...properties} onClick={handleClick}>
        {children}
      </UnstyledButton>
    );
  }
);

RemoverRegulamentoDesafiosButton.displayName = "RemoverRegulamentoDesafiosButton";

export default RemoverRegulamentoDesafiosButton;
